import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import { ReportContext } from './ReportContext';
import { AuthContext } from '../auth/AuthContext';
import { showSwalNotification } from '../utils/SwalResponse';
import LoadingComponent from '../utils/LoadingComponent';
import MediaPreview from '../utils/MediaPreview';
import MarkAsSpam from './forms/MarkAsSpam';
import ReportsPreview from './ReportsPreview';
import ViewLocationMap from '../maps/ViewLocationMap';

const MotionBox = motion.create(Box);

const SingleReportView = () => {
  const [report, setReport] = useState(null);
  const { reportId } = useParams();
  const { fetchSingleReport, loading, verifyReport } = useContext(ReportContext);
  const { currentUser } = useContext(AuthContext);
  const [refetch, setRefetch] = useState(false);

  useEffect(() => {
    async function handleFetch(reportId) {
      const response = await fetchSingleReport(reportId);
      if (response?.status === 'success') {
        setReport(response.data);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        showSwalNotification(response);
        window.history.back();
      }
    }
    if (!!reportId) {
      handleFetch(reportId);
    }
  }, [reportId, refetch]);

  const formatDate = (ts) => new Date(ts).toLocaleString();

  async function handleVerify() {
    if (!!reportId) {
      const response = await verifyReport(reportId);
      if (response?.status === 'success') {
        setRefetch(!refetch);
      }
    }
  }

  const relatedReports = report?.issue?.reports?.filter(
    (r) => r?.report_id !== report?.report_id
  );

  if (!!loading)
    return <LoadingComponent loadingText={'Loading report ....'} />;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <Paper
        sx={{
          p: 4,
          my: 4,
          mx: 'auto',
          maxWidth: '1000px',
          borderRadius: 4,
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        }}
        elevation={0}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight="bold" color="success.main">
            Report Overview
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => window.history.back()}
          >
            ❌
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Report Details */}
        <Grid container spacing={2} sx={{ fontSize: '14px' }}>
          <Grid item xs={12} sm={6}>
            <strong>Report ID:</strong> {report?.report_id}
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Status:</strong>{' '}
            <Chip
              label={report?.status}
              color={
                report?.status === 'Verified'
                  ? 'success'
                  : report?.status === 'Pending'
                  ? 'warning'
                  : 'error'
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Category:</strong> {report?.category}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <strong>Location:</strong> {report?.location}
              {report?.issue && (
                <ViewLocationMap issue={report?.issue }/>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Reported At:</strong> {formatDate(report?.timestamp)}
          </Grid>
        </Grid>

        {/* Description + Media */}
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold" color="success.main">
            Description
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {report?.description}
          </Typography>
          <MediaPreview src={report?.media_url} alt={report?.category} />
        </Box>

        {/* Verify & Spam for Admin */}
        {report?.status === 'Pending' && currentUser?.role === 'admin' && (
          <MotionBox
            mt={4}
            display="flex"
            gap={2}
            justifyContent="center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Button variant="contained" color="success" onClick={handleVerify}>
              Verify
            </Button>
            <MarkAsSpam
              reportId={reportId}
              refetch={refetch}
              setRefetch={setRefetch}
            />
          </MotionBox>
        )}

        {/* Spam Alert */}
        {!!report?.spam_reason && (
          <Box mt={3}>
            <Alert
              severity="warning"
              sx={{
                backgroundColor: 'rgb(181, 75, 75)',
                color: '#ffffff',
                border: '1px solid rgb(113, 65, 65)',
                fontWeight: 500,
              }}
            >
              <Typography fontWeight="bold">Marked as Spam</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {report?.spam_reason}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Issue Details */}
        {report?.issue && (
          <MotionBox mt={5} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              Issue Details
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Status:{' '}
              <Chip
                size="small"
                label={report.issue.status}
                color="info"
              />
            </Typography>
            <Box textAlign="" >
              <Button
                variant="outlined"
                component={Link}
                to={`/issues/${report.issue.issue_id}`}
                color="primary"
              >
                🔗 View more
              </Button>
            </Box>
          </MotionBox>
        )}

        {/* Related Reports */}
        {relatedReports?.length > 0 ? (
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              color="secondary"
              mb={2}
              mt={5}
            >
              Other Reports on This Issue
            </Typography>
            <ReportsPreview reports={relatedReports} />
          </>
        ) : (
          <Typography
            variant="body1"
            fontWeight="bold"
            className="alert alert-info my-4"
            mb={2}
          >
            No Related Reports Found
          </Typography>
        )}
      </Paper>
    </MotionBox>
  );
};

export default SingleReportView;
